import { AffiliateBaseThemeType, ChatBaseThemeType, TapBaseThemeType } from '../theme';
import { CSSColorValue } from './cssValue';

type ThemeMode = 'light' | 'dark';

// palette처럼 light/dark 변환 없이 원본 그대로 유지해야 하는 키 목록
type PreserveKeys = 'palette' | 'device' | 'contestDevice';
const PRESERVE_KEYS = ['palette', 'device', 'contestDevice'];

export type RecursiveThemeValue<T> = {
  [K in keyof T]: K extends PreserveKeys
    ? T[K] // PRESERVE_KEYS에 해당하는 키는 원본 타입 유지
    : T[K] extends { light: string; dark: string }
      ? CSSColorValue // light와 dark가 string인 경우 CSSColorValue로 변환
      : T[K] extends { light: infer L; dark: infer D }
        ? L | D // light와 dark가 있지만 string이 아닌 경우
        : T[K] extends object // 중첩된 객체일 경우 재귀 호출
          ? RecursiveThemeValue<T[K]>
          : T[K]; // 최종 값
};

/**
 * @todo - 베이스 테마 타입을 제대로 구체화해야 함 (지금은 typeof 연산자로 타입 추론)
 */
export const createTheme = <T extends ChatBaseThemeType | AffiliateBaseThemeType | TapBaseThemeType>(
  mode: ThemeMode,
  baseTheme: T
) => {
  // 재귀적으로 theme 값을 탐색하고 반환
  const getValueByMode = <V>(value: V, mode: ThemeMode): RecursiveThemeValue<V> => {
    if (value && typeof value === 'object' && 'light' in value && 'dark' in value) {
      return (value as any)[mode] || (value as any)['light']; // mode에 해당하는 값 반환
    }

    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([k, val]) => {
          // PRESERVE_KEYS에 해당하는 키는 원본 그대로 반환
          if (PRESERVE_KEYS.includes(k)) {
            return [k, val];
          }
          return [k, getValueByMode(val, mode)];
        })
      ) as RecursiveThemeValue<V>;
    }

    return value as RecursiveThemeValue<V>;
  };

  // 3단계 깊이까지 탐색하면서 각 값이 문자열로 반환되도록 설정
  return getValueByMode(baseTheme, mode);
};
